// ---------------------------------------------------------------
// Native push registration (iOS/Android). Asks the OS for permission, gets the
// device's APNs/FCM token, and hands it to the TagAlong backend along with the
// customer's scope so the server can push alerts to a LOCKED or FORCE-CLOSED
// phone. The backend polls Traccar and sends via APNs, so delivery does NOT
// depend on the app being open or the user being signed in — the token stays
// registered server-side until Apple reports it dead.
//
// No-ops on the web (there's no native push there — the in-app AlertWatcher
// handles foreground alerts).
// ---------------------------------------------------------------
import { API_BASE } from './config';
import { getJwt, getScope } from './traccar';

const IS_NATIVE = typeof window !== 'undefined'
  && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

let started = false;
let lastToken = ''; // remembered so we can re-register on every app resume
let apnsEnv = '';   // 'sandbox' | 'production' — detected once from the native build
let driverLink = ''; // OC driver mode: the load link token (no login) — notifications for that load
let driverSession = ''; // company driver signed in to the driver profile: new loads and dispatch messages for every load of theirs
const PLATFORM = (() => { try { return window.Capacitor && window.Capacitor.getPlatform ? window.Capacitor.getPlatform() : 'web'; } catch { return 'web'; } })();   // 'ios' | 'android'

// Ask the native side which APNs environment this build uses (sandbox for
// dev/cable builds, production for TestFlight/App Store). Cached after first
// call. Empty string if it can't be determined — the server then falls back to
// its own default.
async function detectApnsEnv() {
  if (apnsEnv) return apnsEnv;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const AppEnv = registerPlugin('AppEnv');
    const r = await AppEnv.apnsEnvironment();
    apnsEnv = (r && r.environment) || '';
  } catch { apnsEnv = ''; }
  return apnsEnv;
}

// Send (or re-send) the current device token + scope to the backend. Safe to
// call as often as we like — the server de-dupes tokens per user.
async function postToken(token) {
  if (!token || !API_BASE) return;
  lastToken = token;
  if (driverLink) {
    try {
      await fetch(`${API_BASE}/driver/link/${encodeURIComponent(driverLink)}/push`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, platform: PLATFORM, env: PLATFORM === 'ios' ? await detectApnsEnv() : '' }) });
    } catch { /* retried on next open */ }
  }
  if (driverSession) {
    try {
      await fetch(`${API_BASE}/driver/me/push`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${driverSession}` }, body: JSON.stringify({ token, platform: PLATFORM, env: PLATFORM === 'ios' ? await detectApnsEnv() : '' }) });
    } catch { /* retried on next open */ }
  }
  if (!getJwt()) return;            // not a signed-in TagAlong customer (e.g. an OC driver)
  try {
    const scope = getScope() || {};
    const environment = await detectApnsEnv();
    await fetch(`${API_BASE}/push/register`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(getJwt() ? { Authorization: `Bearer ${getJwt()}` } : {}),
      },
      body: JSON.stringify({
        token,
        platform: PLATFORM,
        environment, // 'sandbox' | 'production' | '' → server picks the APNs host
        account: scope.account && scope.account !== '__none__' ? scope.account : '',
        cid: scope.cid || '',
      }),
    });
  } catch { /* will retry next launch / resume */ }
}

// `onOpen(path)` is called when the user taps a notification (path like
// "/map?device=123") so the app can jump straight to the right screen.
export async function registerPush(onOpen) {
  if (!IS_NATIVE || started || !API_BASE) return;
  started = true;
  let PushNotifications;
  try {
    ({ PushNotifications } = await import('@capacitor/push-notifications'));
  } catch {
    return; // plugin not available
  }

  PushNotifications.addListener('registration', (t) => { postToken(t && t.value); });
  PushNotifications.addListener('registrationError', (e) => { console.warn('push registration error', e); });
  // Tapping a notification (lock screen or banner) hands the FULL payload back,
  // so the app can open that alert's detail screen rather than just a route.
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    try {
      const n = (action && action.notification) || {};
      const data = n.data || {};
      if (onOpen) onOpen(data.path || '/car?tagalong', { ...data, body: n.body, title: n.title });
    } catch { /* ignore */ }
  });

  // Android: alerts and dispatch messages ring like a call-out (sound, heads-up)
  if (PLATFORM === 'android') {
    try { await PushNotifications.createChannel({ id: 'default', name: 'Alerts & dispatch messages', importance: 5, visibility: 1, sound: 'default', vibration: true }); } catch { /* older plugin */ }
  }
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'granted') {
      await PushNotifications.register();
    }
  } catch (e) {
    console.warn('push permission/register failed', e);
  }
}

// Re-send the known token to the backend (call on every app resume so the
// server always has the latest token + scope, even if iOS rotated it while the
// app was closed). Cheap no-op if we don't have a token yet.
export async function resendPushToken() {
  if (!IS_NATIVE || !lastToken) return;
  await postToken(lastToken);
}

// Ask the backend to fire a test notification to this user's devices, so the
// user can lock the phone and confirm push + sound work. Returns the server's
// JSON result ({ ok, sent } or { ok:false, reason }).
export async function sendTestPush() {
  if (!API_BASE) return { ok: false, reason: 'no-backend' };
  try {
    const r = await fetch(`${API_BASE}/push/test`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(getJwt() ? { Authorization: `Bearer ${getJwt()}` } : {}),
      },
    });
    return await r.json();
  } catch { return { ok: false, reason: 'network' }; }
}

// Server-side alert history — every push the backend sent, with location. The
// app's own log only covers times the app was open, so this fills the gaps.
// Throws on failure (rather than silently returning []) so the Alerts tab can
// tell the difference between "the server has no alerts yet" and "we never
// reached the server" — those look identical otherwise and are very different
// problems to chase.
export async function fetchAlertHistory() {
  if (!API_BASE) throw new Error('no backend configured');
  const r = await fetch(`${API_BASE}/push/history`, {
    credentials: 'include',
    headers: { ...(getJwt() ? { Authorization: `Bearer ${getJwt()}` } : {}) },
  });
  if (!r.ok) throw new Error(`server said ${r.status}${r.status === 404 ? ' (backend not updated)' : ''}`);
  const j = await r.json();
  return Array.isArray(j.alerts) ? j.alerts : [];
}

// Call after login so the token gets tied to the (now-known) customer scope.
export async function refreshPushRegistration() {
  started = false;
  return registerPush();
}

// Company driver signed in to the profile: this phone hears about a new load and every dispatch message. Tapping one opens the
// profile on the right tab (the path carries ?tab=).
export async function registerDriverProfilePush(sessionToken) {
  if (!IS_NATIVE || !sessionToken) return;
  driverSession = String(sessionToken);
  if (lastToken) { await postToken(lastToken); return; }
  await registerPush((path) => { try { window.location.assign(String(path || '/driver')); } catch { /* ignore */ } });
}

// OC driver mode (no login): get this phone's notifications for the load link —
// dispatch messages and Jarvis check-ins. Tapping one opens the load's driver screen.
export async function registerDriverPush(linkToken) {
  if (!IS_NATIVE || !linkToken) return;
  driverLink = String(linkToken);
  if (lastToken) { await postToken(lastToken); return; }
  await registerPush((path) => { try { window.location.assign(String(path || `/t/${driverLink}`)); } catch { /* ignore */ } });
}
